import { useEffect, useState } from 'react';
import {
  IS_TIPI_ETIKET,
  JOB_STATUSES,
  JOB_STATUS_ETIKET,
  PHASE_TYPES,
  type JobStatus,
  type PhaseType,
} from '@mod/domain';
import { api } from '../lib/api';
import { KapatIkon } from '../lib/ikon';

/**
 * DIŞA AKTARIM KUTUSU — panodaki iş listesinin Excel/PDF çıktısı (istek 11.08.2026).
 *
 * ⚠️ SÜZGEÇ PANODAN BAĞIMSIZDIR. Panonun kova/dönem seçimleri buraya
 *    TAŞINMAZ ve bu bilinçlidir: dışa aktarım çoğunlukla ekrandakinden başka
 *    bir soruyu cevaplar ("geçen ay Fırın 3'te ne söktük"). Panonun süzgeci
 *    sessizce miras alınsaydı, kullanıcı boş sandığı bir formla eksik dosya
 *    üretir ve bunu ancak dosyayı açınca fark ederdi.
 *
 * ⚠️ SÜZGEÇSİZ = TÜMÜ. Hiçbir kutu işaretlenmezse o boyut süzülmez. "Hiçbiri
 *    seçilmedi → hiçbir kayıt" davranışı boş dosya üretirdi; kullanıcının
 *    beklentisi "kısıtlamadım" yönünde.
 */

interface Tanimlar {
  uniteler: { id: string; ad: string; aktif: boolean }[];
  ilgililer: { id: string; ad: string; aktif: boolean }[];
}

const KUTU =
  'rounded-(--radius-mod) border border-line-2 bg-white px-2.5 py-1.5 text-sm outline-none focus:border-navy';

export function DisaAktarKutu({ onKapat }: { onKapat: () => void }) {
  const [tanim, setTanim] = useState<Tanimlar | null>(null);
  const [durum, setDurum] = useState<JobStatus[]>([]);
  const [tip, setTip] = useState<PhaseType[]>([]);
  const [bas, setBas] = useState('');
  const [bit, setBit] = useState('');
  const [ilgili, setIlgili] = useState('');
  const [unite, setUnite] = useState('');
  const [format, setFormat] = useState<'xlsx' | 'pdf'>('xlsx');
  const [mesgul, setMesgul] = useState(false);
  const [hata, setHata] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<Tanimlar>('/tanimlar')
      .then(setTanim)
      .catch((e: Error) => setHata(e.message));
  }, []);

  // ESC ile kapanır — kutu bir katman, sayfa değil.
  useEffect(() => {
    const f = (e: KeyboardEvent) => e.key === 'Escape' && onKapat();
    window.addEventListener('keydown', f);
    return () => window.removeEventListener('keydown', f);
  }, [onKapat]);

  const cevir = <T,>(liste: T[], deger: T): T[] =>
    liste.includes(deger) ? liste.filter((d) => d !== deger) : [...liste, deger];

  /** Tarih aralığı TEK UÇLU olamaz — yarım aralık sessizce tüm kayıtları verir. */
  const aralikYarim = (bas === '') !== (bit === '');
  const tersAralik = bas !== '' && bit !== '' && bas > bit;
  const engel = aralikYarim || tersAralik;

  async function aktar() {
    setHata(null);
    setMesgul(true);
    try {
      const p = new URLSearchParams({ format });
      if (durum.length) p.set('durum', durum.join(','));
      if (tip.length) p.set('tip', tip.join(','));
      if (bas && bit) {
        p.set('bas', bas);
        p.set('bit', bit);
      }
      if (ilgili) p.set('ilgili', ilgili);
      if (unite) p.set('unite', unite);
      await api.indir(`/disa-aktar/isler?${p}`);
      onKapat();
    } catch (e) {
      setHata(e instanceof Error ? e.message : 'Dosya oluşturulamadı');
    } finally {
      setMesgul(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onKapat}
    >
      <div
        className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-(--radius-kart) bg-surface p-5 shadow-(--shadow-mod-h)"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-1 flex items-center justify-between">
          <h3 className="font-semibold text-navy">Dışa Aktar</h3>
          <button onClick={onKapat} aria-label="Kapat" className="text-wait hover:text-ink">
            <KapatIkon boyut={16} />
          </button>
        </div>
        <p className="mb-4 text-xs text-wait">
          Süzgeç <strong>tüm kayıtlara</strong> uygulanır — ekrandaki sayfa ve sıralama çıktıyı
          etkilemez. Boş bırakılan süzgeç kısıtlama yapmaz.
        </p>

        {hata && (
          <p className="mb-3 rounded-(--radius-mod) bg-crit/10 p-2.5 text-sm text-crit">{hata}</p>
        )}

        {/* ── İŞ DURUMU ── */}
        <Grup baslik="İş durumu" secili={durum.length} onTemizle={() => setDurum([])}>
          <div className="flex flex-wrap gap-1.5">
            {JOB_STATUSES.map((d) => (
              <Cip
                key={d}
                etiket={JOB_STATUS_ETIKET[d]}
                secili={durum.includes(d)}
                onSec={() => setDurum((v) => cevir(v, d))}
              />
            ))}
          </div>
        </Grup>

        {/* ── TİP ── */}
        <Grup baslik="Tip" secili={tip.length} onTemizle={() => setTip([])}>
          <div className="flex flex-wrap gap-1.5">
            {PHASE_TYPES.map((t) => (
              <Cip
                key={t}
                etiket={IS_TIPI_ETIKET[t]}
                secili={tip.includes(t)}
                onSec={() => setTip((v) => cevir(v, t))}
              />
            ))}
          </div>
          <p className="mt-1.5 text-xs text-wait">
            Tip işin <strong>o anki durumundan</strong> türetilir — tablodaki TİP sütunuyla aynı.
          </p>
        </Grup>

        {/* ── TARİH ── */}
        <Grup baslik="Açılış tarihi aralığı">
          <div className="flex flex-wrap items-center gap-2">
            <input type="date" value={bas} onChange={(e) => setBas(e.target.value)} className={KUTU} />
            <span className="text-sm text-wait">—</span>
            <input type="date" value={bit} onChange={(e) => setBit(e.target.value)} className={KUTU} />
            {(bas || bit) && (
              <button
                onClick={() => {
                  setBas('');
                  setBit('');
                }}
                className="text-xs font-medium text-wait hover:text-navy"
              >
                temizle
              </button>
            )}
          </div>
          {aralikYarim && (
            <p className="mt-1.5 text-xs text-warn">
              Aralığın iki ucu da girilmeli — tek uç süzgeç uygulamaz.
            </p>
          )}
          {tersAralik && (
            <p className="mt-1.5 text-xs text-warn">Başlangıç, bitişten sonra olamaz.</p>
          )}
          <p className="mt-1.5 text-xs text-wait">
            İşin <strong>açıldığı</strong> tarihe göre süzer (teslim tarihine göre değil).
          </p>
        </Grup>

        {/* ── İLGİLİ + ÜNİTE ── */}
        <div className="grid gap-4 sm:grid-cols-2">
          <Grup baslik="İlgili">
            <select
              value={ilgili}
              onChange={(e) => setIlgili(e.target.value)}
              className={`${KUTU} w-full`}
            >
              <option value="">Tümü</option>
              {tanim?.ilgililer.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.ad}
                </option>
              ))}
            </select>
          </Grup>
          <Grup baslik="Ünite">
            <select
              value={unite}
              onChange={(e) => setUnite(e.target.value)}
              className={`${KUTU} w-full`}
            >
              <option value="">Tümü</option>
              {tanim?.uniteler.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.ad}
                </option>
              ))}
            </select>
          </Grup>
        </div>

        {/* ── FORMAT ── */}
        <Grup baslik="Çıktı biçimi">
          <div className="flex flex-wrap gap-2">
            {(
              [
                ['xlsx', 'Excel (.xlsx)', 'süzülebilir tablo, hesaplanabilir metraj'],
                ['pdf', 'PDF', 'yatay A4, basıma hazır'],
              ] as const
            ).map(([d, e, n]) => (
              <label
                key={d}
                className={`flex cursor-pointer items-start gap-2 rounded-(--radius-mod) border px-3 py-2 text-sm ${
                  format === d ? 'border-navy bg-navy/5' : 'border-line-2 hover:border-navy/40'
                }`}
              >
                <input
                  type="radio"
                  name="format"
                  className="mt-0.5"
                  checked={format === d}
                  onChange={() => setFormat(d)}
                />
                <span>
                  <span className="block font-medium">{e}</span>
                  <span className="block text-xs text-wait">{n}</span>
                </span>
              </label>
            ))}
          </div>
        </Grup>

        <div className="mt-5 flex items-center justify-end gap-2 border-t border-line/70 pt-4">
          <button
            onClick={onKapat}
            className="rounded-(--radius-mod) px-3 py-2 text-sm font-medium text-muted hover:bg-surface-2 hover:text-ink"
          >
            Vazgeç
          </button>
          <button
            onClick={() => void aktar()}
            disabled={mesgul || engel}
            className="rounded-(--radius-mod) bg-navy px-4 py-2 text-sm font-medium text-white disabled:opacity-40"
          >
            {mesgul ? 'Hazırlanıyor…' : 'Dışa Aktar'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Grup({
  baslik,
  secili,
  onTemizle,
  children,
}: {
  baslik: string;
  secili?: number;
  onTemizle?: () => void;
  children: React.ReactNode;
}) {
  return (
    <section className="mb-4">
      <div className="mb-1.5 flex items-center gap-2">
        <h4 className="text-xs font-semibold tracking-wide text-wait uppercase">{baslik}</h4>
        {secili ? (
          <>
            <span className="num text-xs text-navy">{secili} seçili</span>
            <button
              onClick={onTemizle}
              className="text-xs font-medium text-wait hover:text-navy"
            >
              temizle
            </button>
          </>
        ) : (
          <span className="text-xs text-faint">tümü</span>
        )}
      </div>
      {children}
    </section>
  );
}

function Cip({
  etiket,
  secili,
  onSec,
}: {
  etiket: string;
  secili: boolean;
  onSec: () => void;
}) {
  return (
    <button
      onClick={onSec}
      aria-pressed={secili}
      className={`rounded-full border px-2.5 py-1 text-xs font-medium ${
        secili
          ? 'border-navy bg-navy text-white'
          : 'border-line-2 text-ink-2 hover:border-navy hover:text-navy'
      }`}
    >
      {etiket}
    </button>
  );
}
